import React, { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { SubscriptionApiResponse } from 'src/services/responseTypes/subscriptionApiResponse';
import { mixpanelObject } from 'src/utils/analytics';
import { gtm } from 'src/utils/analytics/gtm';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { USER_TOKEN } from 'src/utils/constants/localStorageConstants';

import * as style from './ApplyCodePopupStyle';
import {
  applyPrepaidCode,
  applyPromoCode,
  applyPromoCodeNew,
  redeemPrepaidCode
} from '../../services/apiRequests/SubscriptionService';
import {
  PromoResponseItem,
  PromoResponseItemNew
} from '../../services/responseTypes/applyPromoResponse';
import { colors } from '../../styles/colors';
import {
  MIXPANEL_PROMO_CODE_TYPE,
  MIXPANEL_PROMO_CODE,
  MIXPANEL_SUCCESS,
  MIXPANEL_FAILURE_REASON
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  promoCodeResultEvent,
  popupCTAEvent
} from '../../utils/analytics/mixpanel/mixpanelEventsHelper';
import { DynamicObject } from '../../utils/commonTypes';
import {
  COUPON_CATEGORY,
  COUPON_TYPE
} from '../../utils/constants/stringConstants';
import Modal from '../modal/Modal';

type ApplyCodeProps = {
  show: boolean;
  onClose: () => void;
  updateDiscount?: (
    promoResponse: PromoResponseItem[],
    promoCodeValue: string
  ) => void;
  updateDiscountNew?: (
    promoResponse: PromoResponseItemNew,
    promoCodeValue: string
  ) => void;
  newSubscriptionStrings: DynamicObject;
  countryCode: string;
  stateCode: string;
  checkLoggedIn: (coupon: string) => void;
  prepaidCoupon: string;
  currentSubscription?: SubscriptionApiResponse | null;
  renderContentOnly?: boolean;
  consumptionBuyPlan?: boolean;
  xAccessToken: string;
};

const ApplyCodePopup = ({
  show,
  onClose,
  updateDiscount,
  updateDiscountNew,
  newSubscriptionStrings,
  countryCode,
  stateCode,
  checkLoggedIn,
  prepaidCoupon,
  currentSubscription,
  renderContentOnly,
  consumptionBuyPlan = false,
  xAccessToken
}: ApplyCodeProps) => {
  const [promoCodeValue, setPromocodeValue] = useState(prepaidCoupon);
  const [isPromoPlansError, setIsPromoPlansError] = useState(false);
  const [promoPlansErrorMessage, setPromoPlansErrorMessage] = useState(
    newSubscriptionStrings.SUBSCRIPTION_MSG_INVALIDPREPAIDCODE
  );

  const BackBtn = styled.span`
    ${style.backBtn}
  `;
  const HeaderText = styled.span`
    ${style.headerText}
  `;
  const CodeInput = styled.input`
    ${style.codeInput}
  `;
  const InputContainer = styled.div`
    ${style.inputContainer}
    border: 1px solid ${isPromoPlansError
      ? colors.AccentPromoCodeFailureColor
      : colors.AccentDarkGrayColor};
  `;
  const InputText = styled.div`
    ${style.inputText}
  `;
  const ApplyBtnContainer = styled.div`
    ${style.applyBtnContainer}
  `;

  const onPromoCodeChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const codeRegex = /^[a-zA-Z0-9]+$/;
    if (
      (codeRegex.test(e.target.value) && e.target.value.length <= 30) ||
      e.target.value === ''
    ) {
      setPromocodeValue(e.target.value.trim());
      setIsPromoPlansError(false);
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (promoCodeValue.length > 0 && e.key === 'Enter') {
      // If user presses Enter after giving promo/prepaid code
      consumptionBuyPlan ? onPromoCodeApply() : onPromoCodeApplyNew();
    }
  };

  const onPromoCodeApply = async () => {
    const userToken = getLocalStorage(USER_TOKEN);
    const promoCode = promoCodeValue;
    const applyPrepaidCodeProps = {
      promoCode,
      countryCode,
      stateCode,
      currentSubscription,
      consumptionBuyPlan,
      xAccessToken
    };
    if (promoCode && promoCode.toLowerCase().startsWith('z5')) {
      if (!userToken && !consumptionBuyPlan) {
        onClose();
        checkLoggedIn(promoCode);
        return;
      }
      setIsPromoPlansError(await applyPrepaidCode(applyPrepaidCodeProps));
    } else {
      try {
        const response = await applyPromoCode(promoCodeValue, countryCode);
        if (response?.code) {
          setIsPromoPlansError(true);

          mixpanelObject.setProperty(
            MIXPANEL_PROMO_CODE_TYPE,
            MIXPANEL_PROMO_CODE
          );
          mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
          mixpanelObject.setProperty(
            MIXPANEL_FAILURE_REASON,
            response?.message || "Plan couldn't be found"
          );
          mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, promoCodeValue);
          promoCodeResultEvent();
        } else {
          updateDiscount &&
            updateDiscount(response as PromoResponseItem[], promoCodeValue);
          mixpanelObject.setProperty(
            MIXPANEL_PROMO_CODE_TYPE,
            MIXPANEL_PROMO_CODE
          );
          mixpanelObject.setProperty(MIXPANEL_SUCCESS, true);
          mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'N/A');
          mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, promoCodeValue);
          promoCodeResultEvent();
          gtm.couponCodeAppliedEventGTM(promoCodeValue);
          onClose();
        }
      } catch (error) {
        // in case if the api fail and throws an error
        setIsPromoPlansError(true);
        console.error('plans err =>', error);
        mixpanelObject.setProperty(
          MIXPANEL_PROMO_CODE_TYPE,
          MIXPANEL_PROMO_CODE
        );
        mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
        mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'Invalid Code');
        mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, promoCodeValue);
        promoCodeResultEvent();
      }
    }
    popupCTAEvent();
  };
  /**
   * New Promotion API implementation for Subscription page
   */
  const onPromoCodeApplyNew = async () => {
    const userToken = getLocalStorage(USER_TOKEN);
    if (!userToken) {
      onClose();
      checkLoggedIn(promoCodeValue);
      return;
    }
    try {
      const res = await applyPromoCodeNew(promoCodeValue);
      const { code = '', message = '', promotionDetails } = res;
      if (code === 200) {
        if (
          promotionDetails?.couponType.toLowerCase() ===
            COUPON_TYPE.FREE.toLowerCase() &&
          promotionDetails?.couponCategory.toLowerCase() ===
            COUPON_CATEGORY.UNIQUE.toLowerCase()
        ) {
          setIsPromoPlansError(await redeemPrepaidCode(promoCodeValue));
        } else {
          updateDiscountNew &&
            updateDiscountNew(res as PromoResponseItemNew, promoCodeValue);
          mixpanelObject.setProperty(
            MIXPANEL_PROMO_CODE_TYPE,
            MIXPANEL_PROMO_CODE
          );
          mixpanelObject.setProperty(MIXPANEL_SUCCESS, true);
          mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'N/A');
          mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, promoCodeValue);
          promoCodeResultEvent();
          gtm.couponCodeAppliedEventGTM(promoCodeValue);
          onClose();
        }
      } else {
        setIsPromoPlansError(true);
        setPromoPlansErrorMessage(message);
        mixpanelObject.setProperty(
          MIXPANEL_PROMO_CODE_TYPE,
          MIXPANEL_PROMO_CODE
        );
        mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
        mixpanelObject.setProperty(
          MIXPANEL_FAILURE_REASON,
          message || "Plan couldn't be found"
        );
        mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, promoCodeValue);
        promoCodeResultEvent();
      }
    } catch (error) {
      // in case if the api fail and throws an error
      setIsPromoPlansError(true);
      console.error('plans err =>', error);
      mixpanelObject.setProperty(MIXPANEL_PROMO_CODE_TYPE, MIXPANEL_PROMO_CODE);
      mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
      mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'Invalid Code');
      mixpanelObject.setProperty(MIXPANEL_PROMO_CODE, promoCodeValue);
      promoCodeResultEvent();
    }
    popupCTAEvent();
  };

  const resetPromo = () => {
    setPromocodeValue('');
    setIsPromoPlansError(false);
    popupCTAEvent();
  };

  useEffect(() => {
    if (prepaidCoupon && !currentSubscription) {
      consumptionBuyPlan ? onPromoCodeApply() : onPromoCodeApplyNew();
    }
    if (currentSubscription) {
      setIsPromoPlansError(true);
    }
  }, [prepaidCoupon]);

  const renderPopupContent = () => {
    return (
      <div
        css={style.applyCodePopUpWrapper}
        data-testid="apply-code-popup-container">
        <div css={style.headingContainer}>
          <BackBtn onClick={onClose} data-testid="apply-code-popup-back-btn">
            <img src="/webapp-assets/images/VectorBackArrow.svg" alt="close" />
          </BackBtn>
          <HeaderText data-test-id="headerText">
            {newSubscriptionStrings.CMS_BOTTOMSHEET_APPLYCODE_TEXT}
          </HeaderText>
        </div>
        <InputContainer data-test-id="inputContainer">
          <InputText data-test-id="inputText">
            <CodeInput
              placeholder={newSubscriptionStrings.CMS_CODEENTER_TEXT}
              type="text"
              spellCheck="false"
              autoComplete="off"
              onChange={onPromoCodeChangeHandler}
              value={promoCodeValue}
              autoFocus
              onKeyUp={onKeyUp}
              css={css`
                ${isPromoPlansError ? 'inputError' : ''}
              `}
              data-testid="apply-code-input-field"
            />
          </InputText>
          <ApplyBtnContainer data-test-id="applyBtnContainer">
            {isPromoPlansError ? (
              <>
                <span
                  css={style.error}
                  onClick={resetPromo}
                  data-testid="apply-code-remove-btn">
                  {newSubscriptionStrings.CMS_CODEREMOVE_TEXT}
                </span>
              </>
            ) : (
              <>
                {promoCodeValue.length >= 3 ? (
                  <span
                    onClick={
                      consumptionBuyPlan
                        ? onPromoCodeApply
                        : onPromoCodeApplyNew
                    }
                    data-testid="apply-btn">
                    {newSubscriptionStrings.CMS_CODEAPPLY_TEXT}
                  </span>
                ) : null}
              </>
            )}
          </ApplyBtnContainer>
        </InputContainer>
        {isPromoPlansError && (
          <div css={style.errorMessage}>{promoPlansErrorMessage}</div>
        )}
      </div>
    );
  };

  return (
    <>
      {renderContentOnly ? (
        <>{renderPopupContent()}</>
      ) : (
        <Modal
          show={show}
          onClose={onClose}
          width="376px"
          height="464px"
          background={colors.AppBgColor}
          renderCloseIcon={false}
          closeIconColor={colors.white}
          padding="40px 24px">
          <>{renderPopupContent()}</>
        </Modal>
      )}
    </>
  );
};

export default ApplyCodePopup;
